/**
 * PATH: apps/qa/src/pages/new-inspection/ai-preselection.ts
 * PURPOSE: AI pre-selection logic — analyzes job data to suggest category, class, trades
 */

import type { Job, Site, Client, AIPreselection } from './inspection-types'

export function getSiteName(site: Site | string | null | undefined): string {
  if (!site) return ''
  if (typeof site === 'string') return site
  return site.name || site.address || ''
}

export function getClientName(client: Client | string | null | undefined): string {
  if (!client) return ''
  if (typeof client === 'string') return client
  return client.name || ''
}

/**
 * Analyzes job data and returns intelligent pre-selections for category,
 * building class (SINGLE), and trade tags.
 */
export function analyzeJobForPreselection(job: Job, orgPrimaryTrade?: string | null): AIPreselection {
  const siteName = typeof job.site === 'object' ? job.site?.name || '' : job.site || ''
  const siteAddress = typeof job.site === 'object' ? job.site?.address || '' : ''
  const clientName = typeof job.client === 'object' ? job.client?.name || '' : job.client || ''
  const jobType = job.type?.toLowerCase() || ''
  const jobTags = (job.tags || []).map(t => t.toLowerCase())
  const allText = `${siteName} ${siteAddress} ${clientName} ${jobType} ${jobTags.join(' ')}`.toLowerCase()

  let category: AIPreselection['category'] = 'commercial'
  let projectType: AIPreselection['projectType'] = 'new_construction'
  let buildingClass: string | null = null
  const tradeTags: string[] = []
  const reasons: string[] = []
  let confidence = 0.3

  // 1. Category from keywords
  const infra = ['hospital', 'school', 'university', 'college', 'healthcare', 'medical', 'clinic', 'aged care', 'nursing', 'public', 'government', 'council', 'library', 'community', 'church', 'assembly', 'theatre', 'stadium', 'prison', 'detention', 'spark', 'nel', 'dpie', 'nsw health', 'education', 'childcare', 'kindergarten']
  const resi = ['house', 'home', 'residence', 'dwelling', 'apartment', 'unit', 'townhouse', 'villa', 'duplex', 'granny flat', 'residential', 'domestic']
  const comm = ['office', 'retail', 'shop', 'shopping', 'warehouse', 'factory', 'industrial', 'commercial', 'business', 'corporate', 'carpark', 'car park', 'storage']

  if (infra.some(kw => allText.includes(kw))) { category = 'infrastructure'; reasons.push('Infrastructure project'); confidence += 0.2 }
  else if (resi.some(kw => allText.includes(kw))) { category = 'residential'; reasons.push('Residential project'); confidence += 0.2 }
  else if (comm.some(kw => allText.includes(kw)) || jobType === 'commercial') { category = 'commercial'; reasons.push('Commercial project'); confidence += 0.1 }

  if (jobType === 'civil' || jobType === 'infrastructure') { category = 'infrastructure'; reasons.push('Civil/infrastructure works'); confidence += 0.1 }

  // 2. Project type
  if (jobType === 'remediation' || allText.includes('remediation') || allText.includes('defect') || allText.includes('repair')) { projectType = 'remediation'; reasons.push('Remediation work'); confidence += 0.1 }
  else if (allText.includes('renovation') || allText.includes('refurbish') || allText.includes('retrofit') || allText.includes('upgrade') || allText.includes('fitout') || allText.includes('fit-out')) { projectType = 'renovation'; reasons.push('Renovation project'); confidence += 0.1 }

  // 3. Building class (single, priority order)
  if (allText.match(/\b(hospital|medical|clinic|healthcare|health.?care|surgery|pathology)\b/)) { buildingClass = 'Class 9a'; reasons.push('Healthcare facility'); confidence += 0.3 }
  else if (allText.match(/\b(school|university|college|child.?care|kindergarten|church|community|library|theatre|cinema|stadium|spark|nel)\b/)) { buildingClass = 'Class 9b'; reasons.push('Education/assembly building'); confidence += 0.3 }
  else if (allText.match(/\b(aged.?care|nursing|retirement|elderly)\b/)) { buildingClass = 'Class 9c'; reasons.push('Aged care facility'); confidence += 0.3 }
  else if (allText.match(/\b(house|home|dwelling|residence)\b/) && !allText.includes('apartment')) { buildingClass = 'Class 1a'; reasons.push('Single dwelling'); confidence += 0.25 }
  else if (allText.match(/\b(apartment|unit|flat|multi.?unit|multi.?residential)\b/)) { buildingClass = 'Class 2'; reasons.push('Multi-unit residential'); confidence += 0.25 }
  else if (allText.match(/\b(hotel|motel|accommodation|resort|serviced apartment)\b/)) { buildingClass = 'Class 3'; reasons.push('Hotel/accommodation'); confidence += 0.25 }
  else if (allText.match(/\b(office|professional|corporate|business)\b/) && !allText.includes('home office')) { buildingClass = 'Class 5'; reasons.push('Office building'); confidence += 0.2 }
  else if (allText.match(/\b(shop|retail|cafe|restaurant|showroom|market)\b/)) { buildingClass = 'Class 6'; reasons.push('Retail premises'); confidence += 0.2 }
  else if (allText.match(/\b(carpark|car.?park|parking)\b/) && !allText.includes('residential')) { buildingClass = 'Class 7a'; reasons.push('Car park'); confidence += 0.2 }
  else if (allText.match(/\b(warehouse|storage|distribution|logistics)\b/)) { buildingClass = 'Class 7b'; reasons.push('Warehouse/storage'); confidence += 0.2 }
  else if (allText.match(/\b(factory|manufacturing|laboratory|workshop|production)\b/)) { buildingClass = 'Class 8'; reasons.push('Factory/industrial'); confidence += 0.2 }

  if (!buildingClass) {
    buildingClass = category === 'residential' ? 'Class 1a' : category === 'infrastructure' ? 'Class 9b' : 'Class 5'
    reasons.push('Default for category')
  }

  // 4. Trade tags — org primary trade first
  if (orgPrimaryTrade && !tradeTags.includes(orgPrimaryTrade)) { tradeTags.push(orgPrimaryTrade); reasons.push(`Org specialty: ${orgPrimaryTrade}`); confidence += 0.15 }

  const tradePatterns: [RegExp | string[], string][] = [
    [['waterproof', 'water proof', 'membrane', 'wet area', 'bathroom', 'shower', 'laundry', 'balcony'], 'Waterproofing'],
    [['structural', 'concrete', 'steel', 'foundation', 'beam', 'column', 'slab', 'framing', 'basement'], 'Structural'],
    [['fire', 'sprinkler', 'hydrant', 'smoke', 'alarm', 'egress', 'emergency'], 'Fire Safety'],
    [['electric', 'power', 'wiring', 'switchboard', 'lighting'], 'Electrical'],
    [['plumb', 'pipe', 'drainage', 'sewer', 'water supply', 'sanitary'], 'Plumbing'],
    [['hvac', 'air condition', 'aircon', 'mechanical', 'ventilation', 'heating', 'cooling', 'duct'], 'HVAC'],
    [['civil', 'earthworks', 'road', 'pavement', 'landscap'], 'Civil'],
    [['roof', 'gutter', 'fascia', 'soffit', 'flashing'], 'Roofing'],
    [['cladding', 'facade', 'external wall', 'render', 'curtain wall'], 'Cladding'],
  ]

  for (const [keywords, trade] of tradePatterns) {
    if ((keywords as string[]).some(kw => allText.includes(kw)) || (jobType === 'basement' && trade === 'Waterproofing')) {
      if (!tradeTags.includes(trade)) { tradeTags.push(trade); if (trade === 'Waterproofing' || trade === 'Structural') confidence += 0.1 }
    }
  }
  if (category === 'infrastructure' && !tradeTags.includes('Fire Safety')) tradeTags.push('Fire Safety')

  // 5. Defaults if no trades
  if (tradeTags.length === 0) {
    if (category === 'residential') tradeTags.push('Structural', 'Electrical', 'Plumbing')
    else if (category === 'infrastructure') tradeTags.push('Structural', 'Fire Safety', 'Electrical')
    else tradeTags.push('Electrical', 'Fire Safety', 'HVAC')
  }

  return { category, projectType, buildingClass, tradeTags: [...new Set(tradeTags)], confidence: Math.min(confidence, 0.95), reason: reasons.length > 0 ? reasons.join(', ') : 'Default selections' }
}
